'use client';

import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

// kinotickets.express serves its shop pages with `<body class="bg-transparent overflow-hidden">` and a
// `size-publisher` controller that posts `{ type: 'height', height }` to `window.parent` — once on load
// and again on every body resize (it observes the body with a ResizeObserver). The page is therefore
// built to never scroll itself: the embedding page is expected to grow the iframe to the reported
// height. In a viewport-sized iframe everything below the fold is simply unreachable, which is what
// makes the voucher shop unusable on mobile.
//
// So we listen for that message and give the iframe exactly the height the shop reports, letting our
// own document do the scrolling. Providers that don't publish a height (e.g. the Kinoheld widget's own
// iframes) never send the message and keep the previous viewport-filling behaviour.
const HEIGHT_MESSAGE_TYPE = 'height';

type HeightMessage = {
    type: typeof HEIGHT_MESSAGE_TYPE;
    height: number;
};

function isHeightMessage(data: unknown): data is HeightMessage {
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as HeightMessage).type === HEIGHT_MESSAGE_TYPE &&
        typeof (data as HeightMessage).height === 'number' &&
        (data as HeightMessage).height > 0
    );
}

type ShopIframeProps = {
    url: string;
    title: string;
};

const ShopIframe: React.FC<ShopIframeProps> = ({ url, title }) => {
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const { origin, href } = new URL(url, window.location.href);

        function handleMessage(event: MessageEvent) {
            if (event.origin !== origin || !isHeightMessage(event.data)) {
                return;
            }
            setContentHeight(Math.ceil(event.data.height));
        }

        window.addEventListener('message', handleMessage);

        // The load starts only once we are listening: the element ships in the server HTML, so with a
        // plain `src` the shop would be loading — and publishing its first height — long before
        // hydration on a slow connection, and we would miss that message.
        const iframe = iframeRef.current;
        if (iframe && iframe.src !== href) {
            iframe.src = href;
        }

        return () => window.removeEventListener('message', handleMessage);
    }, [url]);

    return (
        <iframe
            ref={iframeRef}
            title={title}
            className={classnames('w-full border-none', contentHeight === null && 'flex-1')}
            style={contentHeight !== null ? { height: contentHeight } : undefined}
            allow="payment; fullscreen"
        />
    );
};

export default ShopIframe;
